<script setup>
import { TransitionRoot, TransitionChild } from "@headlessui/vue";
import { onClickOutside } from "@vueuse/core";
// import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import emitter from "tiny-emitter/instance";

const emit = defineEmits(["close"]);
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  modalTitle: {
    type: String,
    default: ""
  },
  small: {
    type: Boolean,
    default: false
  },
  confirmation: {
    type: Boolean,
    default: false
  },
  modalBannerUrl: {
    type: String,
    default: ''
  }
});
const target = ref(null);
const { focused } = useFocusWithin(target);

// Close modal
const closeModal = () => {
  // deactivate();
  emit("close");
};

watch(props, (props) => {
  if (props.show) {
    console.log("Modal is shown", props.show);
    emitter.emit("set-focus", { target: target });
  }
});

watch(focused, (focused) => {
  let focusedElement;

  emitter.on("set-focus", (event) => {
    console.log(event);
    console.log(event.target.value === target.value);
    if (event.target.value === target.value && props.show) {
      focusedElement = event.target.value;
    } else {
      focusedElement = null;
    }
  });

  if (focused && focusedElement) {
    // activate();
    console.log("Target contains the focused element", target);
  } else {
    // deactivate();
    console.log("Target does NOT contain the focused element", target);
  }
});

// Close on outside click
onClickOutside(target, () => closeModal());
</script>

<template>
  <TransitionRoot appear :show="show" as="div" class="scrollbar-hide fixed top-0 z-[52] h-screen w-screen"
                  v-scroll-lock="true" tabindex="-1">
    <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
                     leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
      <div class="fixed inset-0 bg-black/80" @click="closeModal()" />
    </TransitionChild>

    <div class="scrollbar-hide fixed inset-0 overflow-y-auto">
      <div class="scrollbar-hide flex min-h-full items-center justify-center text-center md:p-4">
        <TransitionChild as="div" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                         enter-to="opacity-100 scale-100" leave="duration-200 ease-in"
                         leave-from="opacity-100 scale-100"
                         leave-to="opacity-0 scale-95">
          <!-- Modal panel -->
          <div
            class="scrollbar-hide relative flex h-dvh w-screen transform flex-col gap-4 overflow-y-auto overflow-x-hidden bg-brand-card text-left align-middle shadow-xl transition-all sm:h-fit sm:max-w-md sm:rounded-md"
            :class="{
              '!h-fit !w-full !max-w-md': small || confirmation
            }">
            <div>
              <!-- Close button -->
              <button @click="closeModal()" v-if="!confirmation"
                      class="text-md absolute right-3 top-3 z-50 flex items-center justify-center rounded-full transition-all hover:bg-darken-brand-primary text-white/50 hover:text-brand-primary p-1"
                      :class="{
                  'bg-primary text-white/50': modalBannerUrl,
                  'bg-white/10': !modalBannerUrl
                }">
                <Icon name="fa6-solid:xmark" />
              </button>

              <!-- Modal banner -->
              <div v-if="modalBannerUrl"
                   class="relative aspect-[75/32] w-full bg-secondary bg-cover bg-no-repeat bg-center after:absolute after:bottom-0 after:z-10 after:flex after:h-1/2 after:w-full "
                   :style="`background-image: url(${modalBannerUrl});`"></div>

              <!-- Modal title -->
              <div class="relative flex w-full flex-row items-center border-b border-gray-100/10 p-6"
                   :class="{ 'justify-center': modalBannerUrl }" v-if="modalTitle && !$slots['modal-header']">
                <h3 class="text-lg font-bold leading-6 text-white" :class="{ 'text-center': modalBannerUrl }">
                  {{ modalTitle }}
                </h3>
              </div>

              <!-- Modal Header -->
              <div class="flex items-center border-b border-gray-100/10 px-6 py-10" :class="{
                '!py-4': modalBannerUrl
              }" v-if="$slots['modal-header']">
                <div class="flex flex-1 items-center justify-center">
                  <slot name="modal-header" />
                </div>
              </div>
            </div>

            <!-- Modal body -->
            <div class="p-6 md:p-8" :class="{
              '!pt-0': modalBannerUrl
            }">
              <slot />
            </div>

            <!-- Modal footer -->
            <div class="px-6" v-if="!confirmation">
              <slot name="modal-footer" />
            </div>

            <div v-if="confirmation" class="border-t border-gray-100/10 p-6 flex gap-4 justify-end">
              <slot name="confirmation-actions" />
            </div>
          </div>
        </TransitionChild>
      </div>
    </div>
  </TransitionRoot>
</template>
